import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSpring, animated as a, config } from 'react-spring';
import podcastStyles from './podcastdetails.module.css';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ShareIcon from '@mui/icons-material/Share';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import podcasts from '../../data/podcasts';

function PodcastDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, toggleSubscription, toggleFavorite, updateUserListening } = useAuth();
  const [podcast, setPodcast] = useState(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);

  const fadeIn = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: config.gentle
  });

  const slideUp = useSpring({
    from: { transform: 'translateY(100px)', opacity: 0 },
    to: { transform: 'translateY(0px)', opacity: 1 },
    config: config.gentle,
    delay: 200
  });

  useEffect(() => {
    const foundPodcast = podcasts.find(p => p.id === id);
    setPodcast(foundPodcast);
  }, [id]);

  if (!podcast) {
    return <div className={podcastStyles.loading}>Loading...</div>;
  }

  const isSubscribed = user?.subscriptions?.includes(podcast.id);

  const handlePlayEpisode = (episode) => {
    setCurrentlyPlaying(episode);
    setShowPlayer(true);
    if (user) {
      updateUserListening(podcast.id, episode.id, 0);
    }
  };

  const togglePlay = () => {
    setShowPlayer(!showPlayer);
  };

  const isFavorite = (episodeId) => {
    return user?.favorites?.includes(episodeId);
  };

  return (
    <a.div style={fadeIn} className={podcastStyles.container}>
      <div className={podcastStyles.header}>
        <button className={podcastStyles.backBtn} onClick={() => navigate('/')}>
          <ArrowBackIcon /> Back
        </button>
      </div>

      <div className={podcastStyles.hero}>
        <img src={podcast.image} alt={podcast.title} className={podcastStyles.coverImage} />
        <div className={podcastStyles.heroInfo}>
          <h1 className={podcastStyles.title}>{podcast.title}</h1>
          <p className={podcastStyles.publisher}>{podcast.publisher}</p>
          <p className={podcastStyles.description}>{podcast.description}</p>

          <div className={podcastStyles.stats}>
            <span className={podcastStyles.stat}>
              <SubscriptionsIcon /> {podcast.subscribers?.toLocaleString()} subscribers
            </span>
            <span className={podcastStyles.stat}>
              {podcast.totalEpisodes} episodes
            </span>
            <span className={podcastStyles.stat}>
              Rating: {podcast.rating}/5
            </span>
          </div>

          <div className={podcastStyles.actions}>
            <button
              className={podcastStyles.subscribeBtn}
              onClick={() => toggleSubscription(podcast.id)}
            >
              <SubscriptionsIcon />
              {isSubscribed ? 'Subscribed' : 'Subscribe'}
            </button>
            <button className={podcastStyles.shareBtn}>
              <ShareIcon /> Share
            </button>
          </div>
        </div>
      </div>

      <a.div style={slideUp} className={podcastStyles.episodesSection}>
        <h2 className={podcastStyles.sectionTitle}>Episodes</h2>
        <div className={podcastStyles.episodesList}>
          {podcast.episodes.map((episode, index) => (
            <div key={episode.id} className={podcastStyles.episode}>
              <div className={podcastStyles.episodeNumber}>{index + 1}</div>
              <div className={podcastStyles.episodeInfo}>
                <h3 className={podcastStyles.episodeTitle}>{episode.title}</h3>
                <p className={podcastStyles.episodeDesc}>{episode.description}</p>
                <div className={podcastStyles.episodeMeta}>
                  <span>
                    <AccessTimeIcon fontSize="small" /> {episode.duration}
                  </span>
                  <span>{new Date(episode.publishDate).toLocaleDateString()}</span>
                </div>
              </div>
              <div className={podcastStyles.episodeActions}>
                <button
                  className={podcastStyles.playBtn}
                  onClick={() => handlePlayEpisode(episode)}
                >
                  {currentlyPlaying?.id === episode.id && showPlayer ? (
                    <PauseIcon />
                  ) : (
                    <PlayArrowIcon />
                  )}
                </button>
                <button
                  className={podcastStyles.favoriteBtn}
                  onClick={() => toggleFavorite(episode.id)}
                >
                  {isFavorite(episode.id) ? (
                    <FavoriteIcon style={{ color: '#f43f5e' }} />
                  ) : (
                    <FavoriteBorderIcon />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </a.div>

      {showPlayer && currentlyPlaying && (
        <div className={podcastStyles.playerOverlay}>
          <div className={podcastStyles.playerContent}>
            <img src={podcast.image} alt={podcast.title} className={podcastStyles.playerImage} />
            <div className={podcastStyles.playerInfo}>
              <h4>{currentlyPlaying.title}</h4>
              <p>{podcast.title}</p>
            </div>
            <button className={podcastStyles.playerPlayBtn} onClick={togglePlay}>
              {showPlayer ? <PauseIcon /> : <PlayArrowIcon />}
            </button>
          </div>
        </div>
      )}
    </a.div>
  );
}

export default PodcastDetails;
